import React, { useEffect, useState } from "react";

import * as turf from "@turf/turf";
import moment, { Moment } from "moment";

import { useGetFieldShape, useNavigate, useSplitField, useTranslation } from "utils";

import { Area, Coordinates, FieldDetails, Geometry } from "models";

import { Button, Col, DatePicker, Drawer, Flex, Icon, Link, MapInteractive, Modal, Row, Message as message } from "components";

type SplitFieldDrawerProps = {
  field: FieldDetails;
  geometry: Geometry;
  onClose: () => void;
  visible: boolean;
};

type GoogleMapsAPI = {
  drawing: {
    DrawingManager: typeof google.maps.drawing.DrawingManager;
    OverlayType: typeof google.maps.drawing.OverlayType;
  };
  event: {
    addListener: (target: google.maps.drawing.DrawingManager, event: string, handler: (e: google.maps.drawing.OverlayCompleteEvent) => void) => void;
  };
};

export const SplitFieldDrawer = ({ field, onClose, visible }: SplitFieldDrawerProps) => {
  const { t } = useTranslation("farm");
  const navigate = useNavigate();

  const [splitAreas, setSplitAreas] = useState<Area[]>([]);
  const [fieldSplit, setFieldSplit] = useState(false);
  const [lineDrawn, setLineDrawn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [originalShape, setOriginalShape] = useState<Coordinates[]>([{ lat: 0, lng: 0 }]);
  const [shapes, setShapes] = useState<Coordinates[][]>([[{ lat: 0, lng: 0 }], [{ lat: 0, lng: 0 }]]);
  const [showModal, setShowModal] = useState(false);
  const [splitDate, setSplitDate] = useState<Moment>();

  const { mutateAsync: splitField } = useSplitField();
  const { fieldShape } = useGetFieldShape({ fieldId: field.id || "", highQuality: true });

  const setupDrawingAPI = (map: google.maps.Map, google: GoogleMapsAPI) => {
    if (google?.drawing) {
      const drawingManager = new google.drawing.DrawingManager({
        drawingMode: google.drawing.OverlayType.POLYLINE,
        drawingControl: false,
        polylineOptions: {
          strokeOpacity: 0,
          strokeColor: "#ffffff",
          strokeWeight: 2,
          icons: [
            {
              icon: {
                path: "M 0,-1 0,1",
                strokeOpacity: 1,
                strokeColor: "#ffffff",
                scale: 2,
              },
              offset: "0",
              repeat: "10px",
            },
          ],
          clickable: true,
          editable: true,
          draggable: true,
          geodesic: true,
        },
      });

      google.event.addListener(drawingManager, "overlaycomplete", (e) => {
        if (e.type === google.drawing.OverlayType.POLYLINE) {
          setLoading(true);
          const shape = e.overlay as google.maps.Polyline;

          const coordinateArray = shape.getPath().getArray();
          const geometry: number[][] = [];
          coordinateArray.map((item) => {
            const lat = item.lat();
            const lng = item.lng();
            return geometry.push([lat, lng]);
          });
          coordinateArray.reverse().map((item) => {
            const lat = item.lat() + 0.000000001;
            const lng = item.lng() + 0.000000001;
            return geometry.push([lat, lng]);
          });
          geometry.push(geometry[0]);

          const reducedShape = originalShape?.reduce((acc: number[][], shape) => [...acc, [shape.lat, shape.lng]], []);
          const originalPolygon = turf.polygon([reducedShape]);
          const splitPolygon = turf.polygon([geometry]);
          const difference = turf.difference(turf.featureCollection([originalPolygon, splitPolygon]));

          if (
            difference &&
            difference.geometry.coordinates.length === 2 &&
            difference.geometry.coordinates[0][0][0] instanceof Array &&
            difference.geometry.coordinates[1][0][0] instanceof Array
          ) {
            const newShapes = [[], []] as Coordinates[][];
            (difference.geometry.coordinates[0][0] as number[][]).map((coordinates) => newShapes[0].push({ lat: coordinates?.[0], lng: coordinates?.[1] }));
            (difference.geometry.coordinates[1][0] as number[][]).map((coordinates) => newShapes[1].push({ lat: coordinates?.[0], lng: coordinates?.[1] }));

            setShapes(newShapes);
            setFieldSplit(true);
            setLineDrawn(true);
            setTimeout(() => setLoading(false), 10);
          } else {
            setTimeout(() => setLoading(false), 10);
            message({
              content: t("global:errors.split"),
              icon: "error",
            });
          }
        }
      });

      drawingManager.setMap(map);
    }
  };

  const split = async () => {
    setLoading(true);
    try {
      const res = await splitField({
        splitAreas,
        fieldId: field.id,
        splitDate: (splitDate || moment()).format("YYYY-MM-DD HH:mm:ss.SSSZ"),
      });

      navigate(`/farm/field/${res.splitFieldIds[0]}/${res.splitFieldIds[1]}/split-success`);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      setShowModal(false);
      message({
        content: t("global:errors.splitSaveError"),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
    setLoading(false);
  };

  const resetSplit = () => {
    setLoading(true);
    setShapes([originalShape]);
    setLineDrawn(false);
    setFieldSplit(false);
    setTimeout(() => setLoading(false), 10);
  };

  useEffect(() => {
    if (!fieldShape) {
      return;
    }
    const shapesCopy = shapes.length && shapes.length > 0 ? [...shapes] : [];
    shapesCopy.push(fieldShape?.geometry);
    setShapes(shapesCopy);
    setOriginalShape(fieldShape?.geometry);
  }, [fieldShape]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Drawer
        className="split-field"
        dataTestId="split-field-drawer"
        footer={
          <Row align="middle">
            <Col sm={12} md={12}>
              <Link onClick={() => onClose()}>{t("farm:fieldSplitting.cancel")}</Link>
            </Col>
            <Col sm={12} md={12}>
              <Button
                dataTestId="save-split-button"
                block
                disabled={!fieldSplit || !splitDate}
                loading={loading}
                type="primary"
                onClick={() => setShowModal(true)}
              >
                {t("farm:fieldSplitting.saveSplit")}
              </Button>
            </Col>
          </Row>
        }
        onClose={onClose}
        title={t("farm:fieldSplitting.splitField")}
        visible={visible}
      >
        <Flex vertical gap="1rem" className="split-field-container" style={{ height: "100%" }}>
          <div className="map-container">
            {!lineDrawn ? <p className="center label top">{t("farm:fieldSplitting.drawLine")}</p> : null}
            {lineDrawn && !fieldSplit ? <p className="center label top">{t("farm:fieldSplitting.doubleClick")}</p> : null}
            {lineDrawn && fieldSplit ? (
              <p data-testid="reset-split-button" className="center label top pointer" onClick={() => resetSplit()}>
                <Icon iconName="tabler/x" className="inline icon" />
                {t("farm:fieldSplitting.resetSplit")}
              </p>
            ) : null}

            {!loading && field && shapes && (
              // @ts-expect-error need to convert to tsx
              <MapInteractive
                defaultZoom={15}
                disableDrag={true}
                field={field}
                geometries={shapes}
                location={field.location}
                markers={
                  splitAreas.length > 1
                    ? splitAreas.map((area: Area) => ({
                        location: area.center,
                        fieldSize: area.area,
                      }))
                    : null
                }
                setAreas={setSplitAreas}
                setupDrawingAPI={setupDrawingAPI}
                split
                zoomControl={true}
              />
            )}
          </div>
          <Flex vertical gap="1rem" className="split-date-picker">
            <h5>{t("farm:fieldSplitting.splitDate")}</h5>
            <DatePicker initialValue={moment()} onChange={setSplitDate} dataTestId="split-date" disabledDate={(current) => current > moment().endOf("day")} />
          </Flex>
        </Flex>
      </Drawer>
      <Modal
        dataTestId="split-field-modal"
        cancelText={t("farm:fieldSplitting.cancel")}
        description={t("farm:fieldSplitting.modal.description")}
        onCancel={() => setShowModal(false)}
        onOk={() => split()}
        okText={t("farm:fieldSplitting.splitField")}
        showModal={showModal}
        title={t("farm:fieldSplitting.modal.heading")}
        loading={loading}
      />
    </>
  );
};
